import { Tooltip as RadixTooltip } from 'radix-ui';
import { forwardRef, type ReactElement, type ReactNode } from 'react';
import { cn } from '../lib/cn';
import { Kbd } from './Kbd';

/** App-level provider (optional — every <Tooltip> also brings its own). */
export const TooltipProvider = RadixTooltip.Provider;

export interface TooltipProps {
  /** The trigger — must be a single focusable element (it is rendered with `asChild`). */
  children: ReactElement;
  content: ReactNode;
  /** Shortcut rendered as a key cap inside the bubble, e.g. "⌘⇧S". */
  shortcut?: string | undefined;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  /** Delay before showing, in ms (default 300). */
  delayDuration?: number;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  /** Hide the arrow. */
  noArrow?: boolean;
  /** Portal target (default document.body). */
  container?: HTMLElement | null;
}

/** Dark "inverse" bubble with optional key cap. */
export const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  {
    children,
    content,
    shortcut,
    side = 'top',
    align = 'center',
    delayDuration = 300,
    open,
    defaultOpen,
    onOpenChange,
    className,
    noArrow = false,
    container,
  },
  ref,
) {
  return (
    <RadixTooltip.Provider delayDuration={delayDuration}>
      <RadixTooltip.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal container={container}>
          <RadixTooltip.Content
            ref={ref}
            side={side}
            align={align}
            sideOffset={6}
            collisionPadding={8}
            asChild
          >
            <div
              className={cn(
                'z-[70] inline-flex items-center gap-2 whitespace-nowrap rounded-[6px] bg-inverse px-2.5 py-1.5 text-12 text-inverse-text',
                'data-[state=delayed-open]:animate-fade-in data-[state=instant-open]:animate-fade-in',
                className,
              )}
            >
              {content}
              {shortcut ? <Kbd variant="inverse">{shortcut}</Kbd> : null}
              {noArrow ? null : <RadixTooltip.Arrow width={10} height={5} className="fill-inverse" />}
            </div>
          </RadixTooltip.Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
});
